import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API, formatINR, fmtDate, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, XCircle, Pause, Factory, PlayCircle, Truck, Send, Lock, FileText, ClipboardCheck, Wallet } from "lucide-react";

const ACTION_MAP = {
  submit: { label: "Submit for Approval", roles: ["pfi", "operations"], icon: Send, from: ["Draft"], permKey: "submit_pfi" },
  approve: { label: "Approve", roles: ["accounts", "operations"], icon: CheckCircle2, from: ["Submitted", "Accounts Hold"], permKey: "approve" },
  hold: { label: "Put on Hold", roles: ["accounts", "operations"], icon: Pause, from: ["Submitted", "Approved"], permKey: "hold" },
  reject: { label: "Reject", roles: ["accounts", "operations"], icon: XCircle, from: ["Submitted", "Accounts Hold"], permKey: "reject" },
  plan_production: { label: "Plan Production", roles: ["production", "operations"], icon: Factory, from: ["Approved"], permKey: "plan_production" },
  start_production: { label: "Start Production", roles: ["production", "operations"], icon: PlayCircle, from: ["Production Planned"], permKey: "start_production" },
  complete_production: { label: "Mark Production Complete", roles: ["production", "operations"], icon: CheckCircle2, from: ["In Production"], permKey: "complete_production" },
  plan_dispatch: { label: "Plan Dispatch", roles: ["dispatch", "operations"], icon: Truck, from: ["Production Completed"], permKey: "plan_dispatch" },
  dispatch: { label: "Mark Dispatched", roles: ["dispatch", "operations"], icon: Send, from: ["Dispatch Planned"], permKey: "dispatch" },
  close: { label: "Close Order", roles: ["admin", "super_admin"], icon: Lock, from: ["Dispatched"] },
};

const PAYMENT_STATUSES = ["Pending", "Advance Received", "Credit Approved", "Hold", "Rejected"];
const PAYMENT_COLOR = {
  "Pending": "bg-slate-100 text-slate-700 border-slate-200",
  "Advance Received": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Credit Approved": "bg-blue-50 text-blue-700 border-blue-200",
  "Hold": "bg-amber-50 text-amber-700 border-amber-200",
  "Rejected": "bg-red-50 text-red-700 border-red-200",
};

function ActionDialog({ open, onOpenChange, action, order, onDone }) {
  const [remarks, setRemarks] = useState("");
  const [fields, setFields] = useState({});
  useEffect(() => { setRemarks(""); setFields({}); }, [open, action]);
  if (!action) return null;
  const info = ACTION_MAP[action];

  async function run() {
    try {
      const payload = { action, remarks, ...fields };
      const { data } = await api.post(`/orders/${order.id}/action`, payload);
      toast.success(`${info.label} done`); onOpenChange(false); onDone(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle className="heading">{info.label}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {action === "plan_production" && <div><Label>Production Plan Date</Label><Input type="date" value={fields.production_plan_date || ""} onChange={(e) => setFields({ ...fields, production_plan_date: e.target.value })} /></div>}
          {action === "plan_dispatch" && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Dispatch Plan Date</Label><Input type="date" value={fields.dispatch_plan_date || ""} onChange={(e) => setFields({ ...fields, dispatch_plan_date: e.target.value })} /></div>
              <div><Label>Vehicle Number</Label><Input value={fields.vehicle_number || ""} onChange={(e) => setFields({ ...fields, vehicle_number: e.target.value })} /></div>
              <div><Label>Transporter</Label><Input value={fields.transporter || ""} onChange={(e) => setFields({ ...fields, transporter: e.target.value })} /></div>
              <div><Label>Driver</Label><Input value={fields.driver || ""} onChange={(e) => setFields({ ...fields, driver: e.target.value })} /></div>
              <div className="col-span-2"><Label>Route</Label><Input value={fields.route || ""} onChange={(e) => setFields({ ...fields, route: e.target.value })} /></div>
            </div>
          )}
          {action === "dispatch" && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Dispatch Date</Label><Input type="date" value={fields.dispatch_date || ""} onChange={(e) => setFields({ ...fields, dispatch_date: e.target.value })} /></div>
              <div><Label>Vehicle Number</Label><Input value={fields.vehicle_number || ""} onChange={(e) => setFields({ ...fields, vehicle_number: e.target.value })} /></div>
            </div>
          )}
          <div><Label>Remarks</Label><Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={3} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={run} data-testid={`confirm-action-${action}`} className="bg-slate-900 hover:bg-slate-800 text-white">Confirm</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PaymentDialog({ open, onOpenChange, order, onDone }) {
  const [ps, setPs] = useState(order?.payment_status || "Pending");
  const [remarks, setRemarks] = useState("");
  useEffect(() => { setPs(order?.payment_status || "Pending"); setRemarks(""); }, [open, order]);
  async function save() {
    try { const { data } = await api.post(`/orders/${order.id}/payment-status`, { payment_status: ps, remarks }); toast.success("Payment status updated"); onOpenChange(false); onDone(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle className="heading">Update Payment Status</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Payment Status</Label>
            <Select value={ps} onValueChange={setPs}>
              <SelectTrigger data-testid="payment-select"><SelectValue /></SelectTrigger>
              <SelectContent>{PAYMENT_STATUSES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Remarks</Label><Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={3} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} data-testid="save-payment" className="bg-slate-900 hover:bg-slate-800 text-white">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function downloadPdf(path, filename) {
  const token = localStorage.getItem("oms_token");
  fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => r.blob())
    .then((b) => { const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = filename; a.click(); });
}

export default function OrderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, cvShow, canAction } = useAuth();
  const [order, setOrder] = useState(null);
  const [logs, setLogs] = useState([]);
  const [actionOpen, setActionOpen] = useState(false);
  const [action, setAction] = useState(null);
  const [paymentOpen, setPaymentOpen] = useState(false);

  async function load() {
    const [o, l] = await Promise.all([api.get(`/orders/${id}`), api.get(`/orders/${id}/activity`)]);
    setOrder(o.data); setLogs(l.data);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  if (!order) return <div className="text-slate-500">Loading…</div>;

  const allowedActions = Object.entries(ACTION_MAP).filter(([key, info]) => {
    // Phase 4: permission-aware — allow if role matches OR user has the specific action key.
    // Retain admin/super_admin bypass on role check; state-machine is still enforced backend-side.
    const isAdminLike = user.role === "admin" || user.role === "super_admin";
    const roleOK = isAdminLike || info.roles.includes(user.role);
    const permKey = info.permKey;  // optional — set in ACTION_MAP for mapped actions
    const permOK = permKey ? canAction(permKey) : false;
    const stateOK = isAdminLike || info.from.includes(order.status);
    return (roleOK || permOK) && stateOK;
  });
  // Phase 4: set_payment_status now driven by permission; retain role check as fallback
  const canEditPayment = canAction("set_payment_status") || user.role === "admin" || user.role === "super_admin" || user.role === "accounts" || user.role === "operations";
  // Phase 4: commercial-hide driven by show_totals toggle (not hardcoded role)
  const hideCommercial = !cvShow("show_totals");

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <Button variant="ghost" size="icon" onClick={() => nav("/orders")} data-testid="back-btn"><ArrowLeft className="h-4 w-4" /></Button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="heading text-2xl font-bold text-slate-900 mono">{order.pfi_number}</h1>
            <StatusBadge status={order.status} />
            {order.payment_status && (
              <span data-testid="payment-badge" className={`text-xs px-2 py-0.5 rounded-md border ${PAYMENT_COLOR[order.payment_status] || PAYMENT_COLOR.Pending}`}>Payment: {order.payment_status}</span>
            )}
            <span className="text-xs px-2 py-0.5 rounded-md border bg-slate-100 text-slate-700 border-slate-200 uppercase tracking-wide">{(order.rate_type || "distributor").replace("_"," ")}</span>
          </div>
          <div className="text-sm text-slate-500">{order.distributor_name} · {order.area}, {order.region_name || order.region} · {fmtDate(order.order_date)}</div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => downloadPdf(`/orders/${order.id}/pdf/pfi`, `${order.pfi_number}_PFI.pdf`)} data-testid="download-pfi-pdf"><FileText className="h-4 w-4 mr-1" /> PFI PDF</Button>
          {["Production Completed", "Dispatch Planned", "Dispatched", "Closed"].includes(order.status) && (
            <Button variant="outline" size="sm" onClick={() => downloadPdf(`/orders/${order.id}/pdf/loadsheet`, `${order.pfi_number}_LoadSheet.pdf`)} data-testid="download-loadsheet-pdf"><ClipboardCheck className="h-4 w-4 mr-1" /> Load Sheet</Button>
          )}
        </div>
      </div>

      {order.scheme_hints?.length > 0 && (
        <Card className="p-3 border border-amber-200 bg-amber-50">
          <div className="text-xs font-medium text-amber-900 uppercase tracking-wide mb-1">Scheme Recommendations</div>
          <ul className="text-sm text-amber-900 space-y-0.5">
            {order.scheme_hints.map((h, i) => <li key={i}><span className="mono text-xs">{h.sku}</span>: {h.hint}</li>)}
          </ul>
        </Card>
      )}

      {(allowedActions.length > 0 || canEditPayment) && (
        <Card className="p-4 border border-slate-200 shadow-none">
          <div className="flex items-center flex-wrap gap-2">
            <div className="text-xs uppercase tracking-wide text-slate-500 mr-2">Actions</div>
            {canEditPayment && (
              <Button variant="outline" size="sm" onClick={() => setPaymentOpen(true)} data-testid="action-payment"><Wallet className="h-4 w-4 mr-1" /> Update Payment Status</Button>
            )}
            {allowedActions.map(([key, info]) => {
              const Icon = info.icon;
              return (
                <Button key={key} variant={key === "reject" ? "destructive" : "outline"} size="sm"
                  className={key === "approve" ? "bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600" : ""}
                  onClick={() => { setAction(key); setActionOpen(true); }} data-testid={`action-${key}`}>
                  <Icon className="h-4 w-4 mr-1" /> {info.label}
                </Button>
              );
            })}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-5 border border-slate-200 shadow-none lg:col-span-2 space-y-5">
          <div>
            <div className="heading font-semibold text-slate-900 mb-3">Bill To / Header</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              <div><div className="text-slate-500 text-xs">Party</div><div className="text-slate-900">{order.customer?.party_name || order.distributor_name}</div></div>
              <div><div className="text-slate-500 text-xs">GST</div><div className="text-slate-900 mono text-xs">{order.customer?.gst || "-"}</div></div>
              <div><div className="text-slate-500 text-xs">Mobile</div><div className="text-slate-900">{order.customer?.mobile || "-"}</div></div>
              <div><div className="text-slate-500 text-xs">Payment Terms</div><div className="text-slate-900">{order.payment_terms || "-"}</div></div>
              <div><div className="text-slate-500 text-xs">State</div><div className="text-slate-900">{order.state || "-"}</div></div>
              <div><div className="text-slate-500 text-xs">District</div><div className="text-slate-900">{order.district || "-"}</div></div>
              <div><div className="text-slate-500 text-xs">Created By</div><div className="text-slate-900">{order.created_by_name}</div></div>
              <div><div className="text-slate-500 text-xs">Created At</div><div className="text-slate-900">{fmtDateTime(order.created_at)}</div></div>
              <div><div className="text-slate-500 text-xs">Last Updated</div><div className="text-slate-900">{fmtDateTime(order.updated_at)}</div></div>
              <div className="col-span-full"><div className="text-slate-500 text-xs">Remarks</div><div className="text-slate-900">{order.remarks || "-"}</div></div>
              {order.accounts_remarks && <div className="col-span-full"><div className="text-slate-500 text-xs">Accounts Remarks</div><div className="text-slate-900">{order.accounts_remarks}</div></div>}
              {order.production_remarks && <div className="col-span-full"><div className="text-slate-500 text-xs">Production Remarks</div><div className="text-slate-900">{order.production_remarks}</div></div>}
              {order.dispatch_remarks && <div className="col-span-full"><div className="text-slate-500 text-xs">Dispatch Remarks</div><div className="text-slate-900">{order.dispatch_remarks}</div></div>}
            </div>
          </div>

          <div>
            <div className="heading font-semibold text-slate-900 mb-3">Line Items</div>
            <div className="overflow-x-auto border border-slate-200 rounded-md">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="p-3 text-left">SKU</th>
                    <th className="p-3 text-left">Product / Scheme</th>
                    <th className="p-3 text-right">Ord</th>
                    <th className="p-3 text-right">Free</th>
                    <th className="p-3 text-right">Total</th>
                    <th className="p-3 text-right">Rate</th>
                    <th className="p-3 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {order.items.map((i, idx) => (
                    <tr key={idx} className={i.is_free_item ? "bg-emerald-50/50" : ""}>
                      <td className="p-3 mono text-xs">{i.sku}</td>
                      <td className="p-3"><div className="text-slate-900">{i.name}</div>{i.scheme_text && <div className="text-[11px] text-emerald-700">{i.scheme_text}</div>}</td>
                      <td className="p-3 text-right">{i.quantity}</td>
                      <td className="p-3 text-right">{i.free_quantity}</td>
                      <td className="p-3 text-right font-medium">{i.dispatch_quantity}</td>
                      <td className="p-3 text-right">{i.is_free_item ? "—" : (hideCommercial ? <span className="text-slate-400">—</span> : formatINR(i.rate))}</td>
                      <td className="p-3 text-right font-medium">{i.is_free_item ? "—" : (hideCommercial ? <span className="text-slate-400">—</span> : formatINR(i.line_amount))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-slate-200 rounded-md p-3">
              <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Qty Summary</div>
              <div className="text-sm space-y-1">
                <div className="flex justify-between"><span>Ordered</span><span className="font-medium">{order.total_quantity}</span></div>
                <div className="flex justify-between"><span>Free</span><span className="font-medium">{order.total_free_quantity}</span></div>
                <div className="flex justify-between"><span>Dispatch</span><span className="font-medium">{order.total_dispatch_quantity}</span></div>
                <div className="flex justify-between"><span>Weight</span><span className="font-medium">{Number(order.total_weight || 0).toFixed(2)} kg</span></div>
              </div>
            </div>
            {!hideCommercial && (
            <div className="border border-slate-200 rounded-md p-3">
              <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Value Summary</div>
              <div className="text-sm space-y-1">
                <div className="flex justify-between"><span>Gross</span><span className="font-medium">{formatINR(order.gross)}</span></div>
                <div className="flex justify-between"><span>Credit Discount ({order.cash_disc_pct || 0}%)</span><span className="font-medium">-{formatINR(order.disc_amt)}</span></div>
                <div className="flex justify-between"><span>Taxable</span><span className="font-medium">{formatINR(order.taxable)}</span></div>
                {(order.gst_mode || "intra") === "inter" ? (
                  <div className="flex justify-between"><span>IGST ({order.gst_pct || 0}%)</span><span className="font-medium">{formatINR(order.igst_amt || order.gst_amt)}</span></div>
                ) : (
                  <>
                    <div className="flex justify-between"><span>CGST ({((order.gst_pct || 0) / 2)}%)</span><span className="font-medium">{formatINR(order.cgst_amt || (order.gst_amt || 0) / 2)}</span></div>
                    <div className="flex justify-between"><span>SGST ({((order.gst_pct || 0) / 2)}%)</span><span className="font-medium">{formatINR(order.sgst_amt || (order.gst_amt || 0) / 2)}</span></div>
                  </>
                )}
                <div className="flex justify-between pt-2 mt-1 border-t border-slate-200 heading"><span>Grand Total</span><span>{formatINR(order.final || order.grand_total)}</span></div>
              </div>
            </div>
            )}
          </div>
        </Card>

        <Card className="p-5 border border-slate-200 shadow-none">
          <div className="heading font-semibold text-slate-900 mb-4">Activity Timeline</div>
          {logs.length === 0 ? <div className="text-sm text-slate-500">No activity yet.</div> : (
            <ul className="border-l-2 border-slate-200 pl-4 space-y-4">
              {logs.map((l) => (
                <li key={l.id} className="relative">
                  <span className="absolute -left-[21px] top-1 w-3 h-3 rounded-full bg-blue-500 border-2 border-white" />
                  <div className="text-sm text-slate-900"><span className="font-medium">{l.actor_name}</span> · <span className="text-slate-500">{l.actor_role}</span></div>
                  <div className="text-sm text-slate-700">{l.action.replace(/_/g, " ")} {l.old_status ? <>({l.old_status} → {l.new_status})</> : l.new_status ? <>→ {l.new_status}</> : null}</div>
                  {l.remarks && <div className="text-xs text-slate-500 mt-0.5">{l.remarks}</div>}
                  <div className="text-xs text-slate-400 mt-0.5">{fmtDateTime(l.timestamp)}</div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <ActionDialog open={actionOpen} onOpenChange={setActionOpen} action={action} order={order} onDone={() => load()} />
      <PaymentDialog open={paymentOpen} onOpenChange={setPaymentOpen} order={order} onDone={() => load()} />
    </div>
  );
}
